<script setup lang="ts">
import { ElConfigProvider } from 'element-plus'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import { onMounted, provide } from 'vue'
import PermissionGuidanceDialog from '@/components/common/PermissionGuidance/PermissionGuidanceDialog.vue'
import { usePermissionGuidance } from '@/composables'
import { useAppStore, useUserStore } from '@/store'
import { useSystemConfigStore } from '@/store/modules/common/systemConfig'
import { UserRole } from '@/types/auth'
import { ensureRegionData } from '@/utils/region'

useAppStore()
const userStore = useUserStore()
const configStore = useSystemConfigStore()

const globalPermissionGuidance = usePermissionGuidance({
  showDialog: true,
  autoNavigate: false,
  showPreviewMode: true,
  rememberChoice: true,
})

const {
  showGuidanceDialog,
  currentGuidance,
  handleUpgrade,
  handlePreviewMode,
  hideGuidance,
} = globalPermissionGuidance

provide('globalPermissionGuidance', globalPermissionGuidance)

onMounted(async () => {
  // 初始化系统配置
  await configStore.fetchPublicConfig()

  // 如果用户已登录，且当前不在认证相关页面，才加载用户配置（避免引发未授权的 API 告警）
  const isAuthRoute = window.location.pathname.startsWith('/auth/') || window.location.hash.includes('/auth/')
  if (userStore.isLoggedIn && !isAuthRoute) {
    await configStore.fetchUserConfig()
  }

  if (import.meta.env.DEV) {
    // console.info('🚀 [App] 应用初始化完成，权限引导系统已就绪')

    ;(window as any).__GLOBAL_PERMISSION_GUIDANCE__ = globalPermissionGuidance
    ;(window as any).__GLOBAL_USER_STORE__ = userStore
    ;(window as any).__GLOBAL_CONFIG_STORE__ = configStore

    ;(window as any).__DEV_TOOLS__ = {
      switchRole: (role: UserRole) => userStore.devSimulateRoleLogin(role),
      guest: () => userStore.devSimulateRoleLogin(UserRole.GUEST),
      user: () => userStore.devSimulateRoleLogin(UserRole.USER),
      designer: () => userStore.devSimulateRoleLogin(UserRole.DESIGNER),
      manufacturer: () => userStore.devSimulateRoleLogin(UserRole.MANUFACTURER),
      admin: () => userStore.devSimulateRoleLogin(UserRole.ADMIN),
      permissions: () => userStore.devGetPermissionSummary(),
      test: () => userStore.devTestPermissions(),
      clear: () => userStore.devClearLogin(),
      current: () => ({
        role: userStore.userInfo?.role,
        isLoggedIn: userStore.isLoggedIn,
        permissions: userStore.permissions.length,
      }),
      // 配置调试
      config: () => ({
        toggles: configStore.toggles,
        vipPrice: configStore.vipPrice,
        isMaintenanceMode: configStore.isMaintenanceMode,
      }),
      refreshConfig: () => configStore.refreshPublicConfig(),
    }

    // console.info('🛠️ [开发工具] 调试接口已加载到 window.__DEV_TOOLS__')
    // console.info('💡 [使用提示] 控制台快捷命令:')
    // console.info('  - __DEV_TOOLS__.user() 切换到普通用户')
    // console.info('  - __DEV_TOOLS__.designer() 切换到设计师')
    // console.info('  - __DEV_TOOLS__.permissions() 查看权限详情')
    // console.info('  - __DEV_TOOLS__.test() 测试页面权限')
  }
  ensureRegionData()
})
</script>

<template>
  <ElConfigProvider :locale="zhCn">
    <div id="app">
      <router-view v-slot="{ Component, route }">
        <transition :name="(route.meta.transition as string) || 'fade-slide'" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>

      <PermissionGuidanceDialog
        v-model="showGuidanceDialog"
        :guidance="currentGuidance"
        :show-preview-section="true"
        @upgrade="handleUpgrade"
        @preview="handlePreviewMode"
        @cancel="hideGuidance"
      />
    </div>
  </ElConfigProvider>
</template>

<style scoped lang="scss">
#app {
  min-height: 100vh;
  font-family: var(--font-primary);
}
</style>
